import React, { useRef, useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { getVisualSelection, setVisualSelection } from '@aytm/lib/common/content-editable'
import { nextFocusableElement, stopEvent, stopDragOver } from '@aytm/lib/common/func'
import useMultiRef from '@aytm/lib/hooks/useMultiRef'

// styles
import styles from '@aytm/lib/rfd-ui/styles/basic/formatted-input.module.scss'

// mobile browser input duplication fix detection
const duplicationFixEnabled = (() => {
  const userAgentMatch = window.navigator.userAgent.match(/Android.*Chrome\/(\d+)/)
  return userAgentMatch ? parseInt(userAgentMatch[1], 10) <= 74 : false
})()

FormattedInput.propTypes = {
  readOnly: PropTypes.bool,
  formatter: PropTypes.func.isRequired,
  text: PropTypes.string,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onSelect: PropTypes.func,
  onChange: PropTypes.func,
  debounce: PropTypes.number,
  handleEnterKey: PropTypes.func,
  handleTabKey: PropTypes.func,
  formatPastedText: PropTypes.func
}

export default React.memo(
  FormattedInput,
  (prevProps, nextProps) =>
    prevProps.readOnly === nextProps.readOnly &&
    prevProps.text === nextProps.text &&
    prevProps.ariaLabel === nextProps.ariaLabel &&
    prevProps.className === nextProps.className &&
    prevProps.formatter === nextProps.formatter &&
    prevProps.onFocus === nextProps.onFocus &&
    prevProps.onBlur === nextProps.onBlur &&
    prevProps.onSelect === nextProps.onSelect &&
    prevProps.onChange === nextProps.onChange &&
    prevProps.debounce === nextProps.debounce &&
    prevProps.handleEnterKey === nextProps.handleEnterKey &&
    prevProps.handleTabKey === nextProps.handleTabKey &&
    prevProps.formatPastedText === nextProps.formatPastedText
)
export function FormattedInput(props = {}) {
  const {
    text: props_text,
    ariaLabel: props_ariaLabel,
    onFocus: props_onFocus,
    onBlur: props_onBlur,
    onChange: props_onChange,
    onSelect: props_onSelect,
    formatter: props_formatter,
    debounce: props_debounce,
    handleEnterKey: props_handleEnterKey,
    handleTabKey: props_handleTabKey,
    formatPastedText: props_formatPastedText,
    className: props_className,
    readOnly: props_readOnly,
    style: props_style
  } = props

  const [
    inputRef,
    focused,
    selectOnFocus,
    mutationObserver,
    lastInputText,
    lastFormattedHTML,
    lastFormattedText,
    lastSelectionStart,
    lastSelectionEnd,
    mutatedHTML,
    mutatedText
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ] = useMultiRef(null, false, false, null, '', '<br>', '', 0, 0, '<br>', '')

  // update input
  const inputDebounceTimer = useRef(null)
  const updateInput = useCallback(
    (start, deleteCount, insertedChars, { registerChanges = true, forceUpdate = false } = {}) => {
      // console.log(
      //   'updateInput',
      //   {
      //     start,
      //     deleteCount,
      //     insertedChars
      //   },
      //   { registerChanges, forceUpdate }
      // )
      if (inputRef.current && props_formatter && (0 !== deleteCount || 0 !== insertedChars.length || forceUpdate)) {
        const {
          html: htmlAfterFormat = '<br>',
          text: inputTextAfterFormat = '',
          selectionStart: selectionStartAfterFormat = 0,
          selectionEnd: selectionEndAfterFormat = 0,
          valid = false,
          empty = true
        } = props_formatter(start, deleteCount, insertedChars)
        // console.log('formatter output', {
        //   html: htmlAfterFormat,
        //   text: inputTextAfterFormat,
        //   selectionStart: selectionStartAfterFormat,
        //   selectionEnd: selectionEndAfterFormat,
        //   valid,
        //   empty
        // })
        lastFormattedHTML.current = htmlAfterFormat || '<br>'
        if (inputRef.current.innerHTML !== lastFormattedHTML.current) {
          inputRef.current.innerHTML = lastFormattedHTML.current
          lastFormattedHTML.current = inputRef.current.innerHTML
          lastFormattedText.current = inputRef.current.innerText
        }
        if (
          registerChanges &&
          props_onSelect &&
          (lastSelectionStart.current !== selectionStartAfterFormat ||
            lastSelectionEnd.current !== selectionEndAfterFormat)
        ) {
          props_onSelect(selectionStartAfterFormat, selectionEndAfterFormat)
        }
        lastSelectionStart.current = selectionStartAfterFormat
        lastSelectionEnd.current = selectionEndAfterFormat
        setVisualSelection(inputRef.current, selectionStartAfterFormat, selectionEndAfterFormat)
        if (registerChanges && props_onChange && (forceUpdate || lastInputText.current !== inputTextAfterFormat)) {
          if (props_debounce) {
            if (inputDebounceTimer.current) {
              clearTimeout(inputDebounceTimer.current)
            }
            inputDebounceTimer.current = setTimeout(() => {
              inputDebounceTimer.current = null
              props_onChange(inputTextAfterFormat, { valid, empty })
            }, props_debounce)
          } else {
            props_onChange(inputTextAfterFormat, { valid, empty })
          }
        }
        lastInputText.current = inputTextAfterFormat
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props_onChange, props_onSelect, props_formatter, props_debounce]
  )

  // focus / blur
  const onFocus = useCallback(
    event => {
      focused.current = true
      selectOnFocus.current = true
      if (null != inputRef.current && null != mutationObserver.current) {
        mutationObserver.current.observe(inputRef.current, {
          characterData: true,
          childList: true,
          subtree: true
        })
      }
      if (props_onFocus) {
        props_onFocus(event)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props_onFocus]
  )
  const onBlur = useCallback(
    event => {
      if (null != mutationObserver.current) {
        mutationObserver.current.disconnect()
      }
      updateInput(-1, 0, [], { registerChanges: false, forceUpdate: true })
      focused.current = false
      selectOnFocus.current = false
      if (props_onBlur) {
        props_onBlur(event)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props_onBlur, updateInput]
  )

  // manage selection
  const onSelect = useCallback(
    _event => {
      if (null != inputRef.current && focused.current) {
        if (inputRef.current.innerHTML !== lastFormattedHTML.current) {
          // skipped - select is part of mutation
          return
        }
        const [start, end] = getVisualSelection(inputRef.current)
        if (null != start && null != end) {
          if (selectOnFocus.current) {
            selectOnFocus.current = false
            updateInput(end, 0, [], { registerChanges: false, forceUpdate: true })
            if (props_onSelect) {
              props_onSelect(lastSelectionStart.current, lastSelectionEnd.current)
            }
          } else if (lastSelectionStart.current !== start || lastSelectionEnd.current !== end) {
            lastSelectionStart.current = start
            lastSelectionEnd.current = end
            // console.log('onSelect', {
            //   lastSelectionStart: lastSelectionStart.current,
            //   lastSelectionEnd: lastSelectionEnd.current
            // })
            if (props_onSelect) {
              props_onSelect(lastSelectionStart.current, lastSelectionEnd.current)
            }
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props_onSelect, updateInput]
  )

  // register mutation
  const registerMutationDebounceTimer = useRef(null)
  const registerMutation = useCallback(() => {
    if (registerMutationDebounceTimer.current) {
      clearTimeout(registerMutationDebounceTimer.current)
    }
    registerMutationDebounceTimer.current = setTimeout(() => {
      const [mutatedSelectionStart, mutatedSelectionEnd] = getVisualSelection(inputRef.current)
      // console.log('registerMutation', {
      //   lastFormattedHTML: lastFormattedHTML.current,
      //   lastFormattedText: lastFormattedText.current,
      //   mutatedHTML: mutatedHTML.current,
      //   mutatedText: mutatedText.current,
      //   mutatedSelectionStart,
      //   mutatedSelectionEnd,
      //   lastSelectionStart: lastSelectionStart.current,
      //   lastSelectionEnd: lastSelectionEnd.current
      // })
      registerMutationDebounceTimer.current = null

      const mutatedTextBeforeSelection = mutatedText.current.slice(0, mutatedSelectionStart)
      const lastFormattedTextBeforeSelection = lastFormattedText.current.slice(
        0,
        Math.max(0, lastSelectionStart.current)
      )
      const mutatedTextLengthAfterSelection = mutatedText.current.length - mutatedSelectionEnd
      const lastFormattedTextLengthAfterSelection =
        lastFormattedText.current.length - Math.max(0, lastSelectionEnd.current)

      let formatterStart = lastSelectionStart.current
      let deleteCount = lastSelectionEnd.current - lastSelectionStart.current
      const insertedChars = []

      if (mutatedTextBeforeSelection !== lastFormattedTextBeforeSelection) {
        const maxTextsLength = Math.max(mutatedTextBeforeSelection.length, lastFormattedTextBeforeSelection.length)
        for (let i = 0; i < maxTextsLength; i++) {
          if (mutatedTextBeforeSelection[i] !== lastFormattedTextBeforeSelection[i]) {
            formatterStart = i
            if (formatterStart < mutatedSelectionStart) {
              insertedChars.push(...mutatedTextBeforeSelection.slice(formatterStart).split(''))
            }
            if (lastSelectionStart.current > formatterStart) {
              deleteCount += lastSelectionStart.current - formatterStart
            }
            break
          }
        }
      }
      if (mutatedTextLengthAfterSelection < lastFormattedTextLengthAfterSelection) {
        deleteCount += lastFormattedTextLengthAfterSelection - mutatedTextLengthAfterSelection
      }

      if (duplicationFixEnabled && insertedChars.length > 1) {
        let duplicatedCharsAmount = 0
        for (let i = 1; i < insertedChars.length; i++) {
          if (
            lastFormattedTextBeforeSelection.slice(lastFormattedTextBeforeSelection.length - i) ===
            insertedChars.slice(0, i).join('')
          ) {
            duplicatedCharsAmount = i
          }
        }
        updateInput(
          formatterStart,
          deleteCount,
          0 < duplicatedCharsAmount ? insertedChars.slice(duplicatedCharsAmount) : insertedChars,
          { registerChanges: true }
        )
      } else {
        updateInput(formatterStart, deleteCount, insertedChars, { registerChanges: true })
      }
    }, 10)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateInput])

  // manage mutation observer
  useEffect(() => {
    if (mutationObserver.current) {
      mutationObserver.current.disconnect()
    }
    mutationObserver.current =
      'undefined' !== typeof MutationObserver
        ? new MutationObserver(_mutations => {
            const nextHTML = inputRef.current.innerHTML
            if (lastFormattedHTML.current !== nextHTML) {
              mutatedHTML.current = inputRef.current.innerHTML
              mutatedText.current = inputRef.current.innerText
              // console.log('mutation', {
              //   mutatedHTML: mutatedHTML.current,
              //   mutatedText: mutatedText.current
              // })
              registerMutation()
            }
          })
        : null
    if (focused.current && mutationObserver.current && inputRef.current) {
      mutationObserver.current.observe(inputRef.current, {
        characterData: true,
        childList: true,
        subtree: true
      })
    }
    return () => {
      if (mutationObserver.current) {
        mutationObserver.current.disconnect()
        mutationObserver.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef.current, registerMutation])

  // manage props.text and props.formatter changes
  useEffect(() => {
    updateInput(0, 0, (props_text || '').split(''), { registerChanges: true, forceUpdate: true })
    if (!focused.current) {
      // blurred text changes
      updateInput(-1, 0, [], { registerChanges: false, forceUpdate: true })
    }
    return () => {
      updateInput(0, lastFormattedText.current.length, [], { registerChanges: false, forceUpdate: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props_text, props_formatter, updateInput])

  // manage focus
  useEffect(() => {
    if (null != inputRef.current && inputRef.current === document.activeElement) {
      focused.current = true
    }
    return () => {
      focused.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef.current])

  // manage keys
  const onKeyDown = useCallback(
    event => {
      switch (event.key) {
        case 'Enter': {
          if (null != props_handleEnterKey) {
            props_handleEnterKey({
              selectionStart: lastSelectionStart.current,
              selectionEnd: lastSelectionEnd.current,
              html: lastFormattedHTML.current,
              text: lastFormattedText.current,
              ctrlKey: event.ctrlKey,
              shiftKey: event.shiftKey,
              altKey: event.altKey,
              metaKey: event.metaKey,
              updateInput
            }) && stopEvent(event)
          } else if (event.ctrlKey || event.shiftKey || event.altKey || event.metaKey) {
            stopEvent(event)
            break
          } else {
            stopEvent(event)
            const selectionStartBeforeUpdate = lastSelectionStart.current
            const selectionEndBeforeUpdate = lastSelectionEnd.current
            updateInput(lastSelectionStart.current, lastSelectionEnd.current - lastSelectionStart.current, ['\n'], {
              registerChanges: false
            })
            if (
              selectionStartBeforeUpdate === lastSelectionStart.current &&
              selectionEndBeforeUpdate === lastSelectionEnd.current
            ) {
              // selection has not changed - jump to next focusable item
              const nextFocusable = nextFocusableElement(inputRef.current)
              if (null != nextFocusable && nextFocusable.focus) {
                nextFocusable.focus()
              } else if (inputRef.current && inputRef.current.blur) {
                inputRef.current.blur()
              }
            }
          }
          break
        }
        case 'Tab': {
          if (null != props_handleTabKey) {
            props_handleTabKey({
              selectionStart: lastSelectionStart.current,
              selectionEnd: lastSelectionEnd.current,
              html: lastFormattedHTML.current,
              text: lastFormattedText.current,
              ctrlKey: event.ctrlKey,
              shiftKey: event.shiftKey,
              altKey: event.altKey,
              metaKey: event.metaKey,
              updateInput
            }) && stopEvent(event)
          } else if (event.ctrlKey || event.shiftKey || event.altKey || event.metaKey) {
            break
          } else {
            const selectionStartBeforeUpdate = lastSelectionStart.current
            const selectionEndBeforeUpdate = lastSelectionEnd.current
            updateInput(lastSelectionStart.current, lastSelectionEnd.current - lastSelectionStart.current, ['\t'], {
              registerChanges: false
            })
            if (
              selectionStartBeforeUpdate !== lastSelectionStart.current ||
              selectionEndBeforeUpdate !== lastSelectionEnd.current
            ) {
              // selection changed - skip default action
              stopEvent(event)
            }
          }
          break
        }
        case 'Backspace': {
          stopEvent(event)
          if (event.ctrlKey || event.altKey || event.metaKey) {
            break
          }
          updateInput(
            lastSelectionStart.current,
            lastSelectionEnd.current !== lastSelectionStart.current
              ? lastSelectionEnd.current - lastSelectionStart.current
              : -1,
            [],
            { registerChanges: true }
          )
          break
        }
        case 'Delete': {
          stopEvent(event)
          if (event.ctrlKey || event.altKey || event.metaKey) {
            break
          }
          updateInput(
            lastSelectionStart.current,
            lastSelectionEnd.current !== lastSelectionStart.current
              ? lastSelectionEnd.current - lastSelectionStart.current
              : 1,
            [],
            { registerChanges: true }
          )
          break
        }
        case ' ': {
          stopEvent(event)
          if (event.ctrlKey || event.altKey || event.metaKey) {
            break
          }
          updateInput(lastSelectionStart.current, lastSelectionEnd.current - lastSelectionStart.current, [' '], {
            registerChanges: true
          })
          break
        }
        case 'y':
        case 'z': {
          if (event.ctrlKey) {
            stopEvent(event)
          }
          break
        }
        default: {
          break
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props_handleEnterKey, props_handleTabKey, updateInput]
  )

  const onPaste = useCallback(
    event => {
      stopEvent(event)
      let pastedText = ''
      try {
        pastedText = (event.clipboardData || window.clipboardData).getData('text') || ''
      } catch {
        pastedText = ''
      }
      if (pastedText.length) {
        updateInput(
          lastSelectionStart.current,
          lastSelectionEnd.current - lastSelectionStart.current,
          props_formatPastedText ? props_formatPastedText(pastedText).split('') : pastedText.split(''),
          { registerChanges: true }
        )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props_formatPastedText, updateInput]
  )

  // manage className
  const className = useMemo(
    () => `rfd-ui-formatted-input ${styles.component}${props_className ? ' ' + props_className : ''}`,
    [props_className]
  )

  return (
    <div
      aria-label={props_ariaLabel}
      contentEditable={!props_readOnly}
      className={className}
      data-testid="SW-Formatted-Input"
      style={props_style}
      ref={inputRef}
      onFocus={onFocus}
      onBlur={onBlur}
      onSelect={onSelect}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      onDragOver={stopDragOver}
      onDrop={stopEvent}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck="false"
      key="formatted-input"
    />
  )
}
